import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import { renderEmailEvent } from '../../services/emailTemplates';

/**
 * Confirming a decision on a paper.
 *
 * This replaced `window.confirm()`, which asked "Accept this paper?" and gave
 * you OK or Cancel. Two things were wrong with that. It's the browser's own
 * dialog — unstyled, announcing "localhost:4028 says", and unreadable on a
 * phone. More importantly it confirmed the *click* rather than the
 * *consequence*: the moment you pressed OK an email went to the author, and
 * nothing on screen had told you that, let alone what it would say.
 *
 * So this shows the outcome, the email that will be sent, and lets the chair
 * edit it before it goes. A decision email is often the only thing an author
 * ever reads from a conference; being able to add a sentence to it matters more
 * than the click-through speed.
 *
 * The five outcomes map onto the data like this:
 *   accept        → status accepted, no revision
 *   minor / major → status accepted, revision_severity set
 *                   (the paper is IN; the revision is a condition of it)
 *   reject        → status rejected (first-time review outcome)
 *   withdraw      → status rejected (an EARLIER acceptance is being undone,
 *                   typically because a conditional revision was never
 *                   reflected in the camera-ready — gets its own email
 *                   template rather than reusing "reject", because the author
 *                   already received an acceptance email and the message has
 *                   to acknowledge that instead of re-explaining a review
 *                   outcome that already happened)
 */

export const DECISION_OUTCOMES = [
  { key: 'accept',   tone: 'success', icon: 'CheckCircle2', emailEvent: 'paper_accepted' },
  { key: 'minor',    tone: 'primary', icon: 'PencilLine',   emailEvent: 'paper_accepted_minor_revision', severity: 'minor' },
  { key: 'major',    tone: 'accent',  icon: 'PencilRuler',  emailEvent: 'paper_accepted_major_revision', severity: 'major' },
  { key: 'reject',   tone: 'error',   icon: 'XCircle',      emailEvent: 'paper_rejected' },
  { key: 'withdraw', tone: 'error',   icon: 'Undo2',        emailEvent: 'acceptance_withdrawn' },
];

/**
 * Author name, from whichever shape the caller hands us.
 *
 * The conference table exposes `author: { firstName, lastName }`; other
 * screens use a flat `authorName`. Reading only one of them produced emails
 * addressed "Dear ," — worth being generous about the input here, because the
 * failure lands in a stranger's inbox.
 */
const authorNameOf = (paper) => {
  const a = paper?.author;
  const full = [a?.firstName, a?.lastName].filter(Boolean).join(' ').trim();
  return paper?.authorName || full || a?.name || a?.email || '';
};

const TONE = {
  success: 'text-success',
  primary: 'text-primary',
  accent: 'text-accent',
  error: 'text-error',
};

const DecisionDialog = ({
  open,
  outcome,          // one of DECISION_OUTCOMES
  paper,            // single-paper mode
  papers,           // bulk mode: apply the same outcome to all of these
  conference,
  customTemplate,   // the conference's saved override for this event, if any
  onCancel,
  onConfirm,
  saving = false,
}) => {
  const { t } = useTranslation('conferences');
  const [note, setNote] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [showEmail, setShowEmail] = useState(false);

  const bulk = Array.isArray(papers) && papers.length > 0;
  // In bulk the preview needs *a* paper to render against; the first one stands
  // in, and the dialog says so rather than pretending it's the only email.
  const sample = bulk ? papers[0] : paper;
  const needsNote = outcome?.severity != null;

  // Re-render the email whenever the note changes, so the chair sees exactly
  // what the author will receive rather than a template with placeholders.
  const rendered = useMemo(() => {
    if (!outcome || !sample) return { subject: '', message: '' };
    const out = renderEmailEvent(outcome.emailEvent, customTemplate, {
      authorName: authorNameOf(sample),
      paperTitle: sample?.title || '',
      conferenceName: conference?.name || '',
      // 'withdraw' has no note field of its own (needsNote is false for it) —
      // it's referencing a revision that was already asked for, so pull that
      // from the paper's own record rather than the (empty) note textarea.
      revisionNote: outcome?.key === 'withdraw'
        ? (sample?.revisionNote || t('decisionDialog.notePlaceholderShort', '(no specific changes listed)'))
        : (note || t('decisionDialog.notePlaceholderShort', '(no specific changes listed)')),
      cameraReadyDeadline: conference?.cameraReadyDeadline
        ? new Date(conference.cameraReadyDeadline).toLocaleDateString()
        : t('decisionDialog.noDeadline', 'the camera-ready deadline'),
    });
    return out || { subject: '', message: '' };
  }, [outcome, sample, conference, customTemplate, note, t]);

  // Reset each time the dialog opens for a different paper/outcome.
  useEffect(() => {
    if (!open) return;
    setNote('');
    setShowEmail(false);
  }, [open, paper?.id, bulk, outcome?.key]);

  useEffect(() => {
    setSubject(rendered.subject || '');
    setMessage(rendered.message || '');
  }, [rendered.subject, rendered.message]);

  useEffect(() => {
    const onEsc = (e) => e?.key === 'Escape' && !saving && onCancel?.();
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, saving, onCancel]);

  if (!open || !outcome || (!paper && !bulk)) return null;

  const label = t(`decisionDialog.outcomes.${outcome.key}`, outcome.key);

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => !saving && onCancel?.()} />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="decision-dialog-title"
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card border border-border rounded-lg shadow-elevation-4 motion-rise"
      >
        <div className="p-5 border-b border-border">
          <div className="flex items-start gap-3">
            <Icon name={outcome.icon} size={20} className={`${TONE[outcome.tone]} mt-0.5 flex-shrink-0`} />
            <div className="min-w-0">
              <h2 id="decision-dialog-title" className="text-base font-semibold text-foreground">
                {label}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5 break-words">
                {bulk
                  ? t('decisionDialog.bulkSubtitle', '{{count}} selected papers', { count: papers.length })
                  : paper?.title}
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {needsNote && (
            <Textarea
              label={t('decisionDialog.noteLabel', 'What should the author change?')}
              description={
                bulk
                  ? t('decisionDialog.noteHelpBulk', 'The same text goes to every author you selected, so keep it general — anything paper-specific belongs in an individual decision.')
                  : t('decisionDialog.noteHelp', 'This goes into the email, so write it for the author rather than as a note to yourself.')
              }
              rows={4}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('decisionDialog.notePlaceholder', 'e.g. Expand the evaluation section and clarify the threat model.')}
            />
          )}

          {/* The consequence, stated plainly. */}
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <div className="flex items-start gap-2">
              <Icon name="Mail" size={15} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground">
                  {bulk
                    ? t('decisionDialog.willEmailBulk', 'An email goes to each of the {{count}} authors, addressed to them individually.', { count: papers.length })
                    : t('decisionDialog.willEmail', 'An email will be sent to {{name}}.', {
                        name: authorNameOf(paper) || t('decisionDialog.theAuthor', 'the author'),
                      })}
                </p>
                <button
                  type="button"
                  onClick={() => setShowEmail((v) => !v)}
                  className="text-xs text-primary hover:underline mt-1"
                >
                  {showEmail
                    ? t('decisionDialog.hideEmail', 'Hide the email')
                    : t('decisionDialog.reviewEmail', 'Read or edit it first')}
                </button>
              </div>
            </div>

            {showEmail && (
              <div className="mt-3 space-y-3">
                <Textarea
                  label={t('decisionDialog.subject', 'Subject')}
                  rows={2}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <Textarea
                  label={t('decisionDialog.body', 'Message')}
                  rows={12}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  {bulk
                    ? t('decisionDialog.editScopeBulk', 'Shown for the first paper. Each author receives their own copy with their name and title filled in.')
                    : t('decisionDialog.editScope', 'Edits apply to this email only. To change the wording for every paper, use the Emails tab.')}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="p-5 border-t border-border flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            {t('decisionDialog.cancel', 'Cancel')}
          </Button>
          <Button
            variant={(outcome.key === 'reject' || outcome.key === 'withdraw') ? 'destructive' : 'default'}
            loading={saving}
            iconName={outcome.icon}
            iconPosition="left"
            onClick={() =>
              onConfirm?.({
                outcome,
                severity: outcome.severity ?? null,
                note: note || null,
                email: { subject, message },
                // In bulk mode, tells the caller whether the chair hand-edited
                // this text (rendered against the sample paper only) — if so,
                // that shared text should go to every author verbatim, same as
                // today; if not, the caller re-renders it per author instead of
                // sending everyone the sample paper's title and name.
                edited: subject !== rendered.subject || message !== rendered.message,
              })
            }
          >
            {t('decisionDialog.confirm', 'Send and record')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DecisionDialog;
